const CACHE_NAME = "coach-pictures-v1";

// Layer 1: in-memory (instant, same session)
const blobUrlCache = new Map();

// Deduplicates concurrent calls for the same email
const inFlight = new Map();

async function _load(email) {
  const cacheKey = `https://coach-picture-cache/${encodeURIComponent(email)}`;

  // Layer 2: Cache Storage (persists across page loads — no Google hit on refresh)
  if ("caches" in window) {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);
    if (cached) {
      const blob = await cached.blob();
      const objectUrl = URL.createObjectURL(blob);
      blobUrlCache.set(email, objectUrl);
      return objectUrl;
    }
  }

  // Layer 3: fetch from API → get Google URL → fetch image blob
  const apiRes = await fetch(
    `https://coachconnect.gracepapers.com.au/api/picture/${encodeURIComponent(
      email
    )}`
  );
  if (!apiRes.ok) throw new Error("Non-OK response");
  const data = await apiRes.json();
  if (!data?.picture) throw new Error("No picture");

  const imgRes = await fetch(data.picture);
  if (!imgRes.ok) throw new Error("Image fetch failed");

  // Store image bytes in Cache Storage so future page loads skip Google entirely.
  // Wrapped in try/catch — Google responses may carry Cache-Control: no-store
  // which causes cache.put() to throw, but we still want the blob either way.
  if ("caches" in window) {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(cacheKey, imgRes.clone());
    } catch (e) {
      // Cache write rejected (no-store, quota exceeded, etc.) — continue without caching
      console.warn(`Coach picture for ${email} not cached`, e);
    }
  }

  const blob = await imgRes.blob();
  const objectUrl = URL.createObjectURL(blob);
  blobUrlCache.set(email, objectUrl);
  return objectUrl;
}

/**
 * Fetches a coach's profile picture by email.
 * Cache hierarchy: in-memory → Cache Storage → Google (first load only).
 * Concurrent calls for the same email share a single in-flight Promise.
 *
 * @param {string} email
 * @returns {Promise<string>} Blob object URL
 */
export function fetchCoachPicture(email) {
  if (blobUrlCache.has(email)) {
    return Promise.resolve(blobUrlCache.get(email));
  }

  if (inFlight.has(email)) {
    return inFlight.get(email);
  }

  const promise = _load(email).finally(() => inFlight.delete(email));
  inFlight.set(email, promise);
  return promise;
}
